import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { AppShell } from '@/admin/components/app-shell'
import { Button } from '@/shared/components/ui/button'
import { useSession } from '@/admin/hooks/use-session'
import { useTour } from './use-tour'
import { TOUR_CHAPTERS } from './tour-chapters'
import type { TourChapter } from './tour-chapters'
import { useFeatureFlags } from '@/admin/hooks/use-feature-flag'
import { Check, Clock, ChevronDown } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

// ── Confetti engine (zero dependencies) ──────────────────

const BRAND_COLOURS = [
  '#1C5A48', // iWarranty green
  '#C5A94C', // gold
  '#A8D5BA', // pale green
  '#E8DCC8', // warm sand
  '#D4C5A0', // muted gold
  '#2D7A5F', // light green
]

interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  size: number
  colour: string
  rotation: number
  rotationSpeed: number
  opacity: number
}

function launchConfetti(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  canvas.width = window.innerWidth
  canvas.height = window.innerHeight

  const particles: Particle[] = []
  const count = 150

  for (let i = 0; i < count; i++) {
    particles.push({
      x: Math.random() * canvas.width,
      y: -10 - Math.random() * 200,
      vx: (Math.random() - 0.5) * 6,
      vy: Math.random() * 3 + 2,
      size: Math.random() * 6 + 4,
      colour: BRAND_COLOURS[Math.floor(Math.random() * BRAND_COLOURS.length)],
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 10,
      opacity: 1,
    })
  }

  let frame = 0
  const maxFrames = 180 // ~3 seconds at 60fps

  function animate() {
    if (!ctx) return
    frame++
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    for (const p of particles) {
      p.x += p.vx
      p.vy += 0.05 // gravity
      p.y += p.vy
      p.rotation += p.rotationSpeed
      if (frame > maxFrames - 60) {
        p.opacity = Math.max(0, p.opacity - 0.02)
      }

      ctx.save()
      ctx.translate(p.x, p.y)
      ctx.rotate((p.rotation * Math.PI) / 180)
      ctx.globalAlpha = p.opacity
      ctx.fillStyle = p.colour
      ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6)
      ctx.restore()
    }

    if (frame < maxFrames) {
      requestAnimationFrame(animate)
    } else {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
    }
  }

  requestAnimationFrame(animate)
}

// ── Tour Hub Page ────────────────────────────────────────

function ChapterCard({
  chapter,
  isComplete,
  isSoon,
  onStart,
}: {
  chapter: TourChapter
  isComplete: boolean
  isSoon: boolean
  onStart: () => void
}) {
  const { t } = useTranslation()
  const Icon = chapter.icon
  return (
    <button
      disabled={isSoon}
      onClick={() => { if (!isSoon) onStart() }}
      className={cn(
        'group relative flex flex-col rounded-[12px] border-[0.5px] p-5 text-left transition-all duration-300',
        isSoon
          ? 'cursor-default border-[var(--rule)] bg-[var(--bg-subtle)] opacity-40'
          : isComplete
            ? 'cursor-pointer border-[var(--gold-border)] bg-[var(--gold-bg)] hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(26,26,24,0.08)]'
            : 'cursor-pointer border-[var(--rule)] bg-white hover:-translate-y-1 hover:border-[var(--gold-border)] hover:shadow-[0_12px_40px_rgba(26,26,24,0.08)]',
      )}
    >
      {isComplete && !isSoon && (
        <div className="absolute top-4 right-4 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--green)]">
          <Check className="h-3 w-3 text-white" />
        </div>
      )}
      {isSoon && (
        <div className="absolute top-4 right-4 flex items-center gap-1 rounded-full bg-white/80 px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          <Clock className="h-2.5 w-2.5" />
          {t('tour.hub.comingSoon', 'Coming soon')}
        </div>
      )}
      <div
        className={cn(
          'mb-4 flex h-11 w-11 items-center justify-center rounded-[10px]',
          isSoon
            ? 'bg-[var(--bg)] text-[var(--ink-3)]'
            : isComplete
              ? 'bg-white text-[var(--green)]'
              : 'bg-[var(--bg-subtle)] text-[var(--ink-3)] group-hover:bg-[var(--gold-bg)] group-hover:text-[var(--gold-ink)]',
        )}
      >
        <Icon className="h-5 w-5" />
      </div>
      <p className={cn('mb-1.5 text-[14px] font-medium', isSoon ? 'text-[var(--ink-3)]' : 'text-[var(--ink)]')}>
        {t(`tour.chapter.${chapter.id}.title`, chapter.title)}
      </p>
      <p className="mb-4 flex-1 text-[12px] leading-relaxed text-[var(--ink-3)]">
        {t(`tour.chapter.${chapter.id}.description`, chapter.description)}
      </p>
      <span className={cn('text-[10px] uppercase tracking-[0.08em]', isComplete ? 'text-[var(--gold-ink)]' : 'text-[var(--ink-3)]')}>
        {isSoon
          ? t('tour.hub.phase2', 'Phase 2')
          : isComplete
            ? t('tour.hub.complete', 'Complete')
            : t('tour.hub.stepsCount', '{{count}} steps', { count: chapter.steps.length })}
      </span>
    </button>
  )
}

export function TourHubPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: session } = useSession()
  const { completedChapters, startChapter, resetTour } = useTour()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const confettiFired = useRef(false)
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null)
  const featureFlags = useFeatureFlags()

  // Only show chapters whose gating flags are enabled for this tenant.
  // A chapter with no `flags` is always visible; otherwise it's shown when
  // at least one of its flags is enabled.
  const visibleChapters = TOUR_CHAPTERS.filter(
    (c) => !c.flags || c.flags.some((f) => featureFlags[f] === true),
  )

  const activeChapters = visibleChapters.filter((c) => !c.comingSoon)
  const completedCount = activeChapters.filter((c) => completedChapters.has(c.id)).length
  const totalCount = activeChapters.length
  const allDone = completedCount >= totalCount

  const groups = new Map<string, TourChapter[]>()
  for (const c of visibleChapters) {
    if (c.group) {
      const list = groups.get(c.group) ?? []
      list.push(c)
      groups.set(c.group, list)
    }
  }

  const handleStart = useCallback((chapter: TourChapter) => {
    navigate(chapter.route)
    setTimeout(() => startChapter(chapter.id), 600)
  }, [navigate, startChapter])

  const firstName = session?.user?.first_name ?? t('tour.hub.defaultName', 'there')

  // Fire confetti when all chapters complete
  const fireConfetti = useCallback(() => {
    if (canvasRef.current && !confettiFired.current) {
      confettiFired.current = true
      launchConfetti(canvasRef.current)
    }
  }, [])

  useEffect(() => {
    if (allDone) {
      // Small delay so the page renders first
      const timer = setTimeout(fireConfetti, 400)
      return () => clearTimeout(timer)
    } else {
      confettiFired.current = false
    }
  }, [allDone, fireConfetti])

  return (
    <AppShell>
      {/* Confetti canvas - fixed overlay, pointer-events-none */}
      <canvas
        ref={canvasRef}
        className="pointer-events-none fixed inset-0 z-[60]"
      />

      <main className="mx-auto max-w-[1100px] px-8 py-12">
        {/* Header */}
        {allDone ? (
          /* ── Completion state ── */
          <div className="mx-auto mb-12 max-w-[600px] text-center">
            <h1 className="font-display text-[36px] font-light text-[var(--ink)] mb-3">
              {t('tour.hub.allSetTitle', "You're all set, {{name}}", { name: firstName })}
            </h1>
            <p className="mb-6 text-[13px] leading-relaxed text-[var(--ink-2)]">
              {t('tour.hub.allSetBody', "You've completed every chapter. Explore at your own pace, or retake any walkthrough below.")}
            </p>
            <Button variant="ghost" size="xs" onClick={resetTour}>
              {t('tour.hub.resetProgress', 'Reset progress')}
            </Button>
          </div>
        ) : (
          /* ── Welcome state ── */
          <>
            <div className="mb-2">
              <h1 className="font-display text-[36px] font-light text-[var(--ink)]">
                {t('tour.hub.welcomeTitle', 'Welcome to iWarranty, {{name}}', { name: firstName })}
              </h1>
            </div>
            <p className="mb-10 text-[13px] text-[var(--ink-3)]">
              {t('tour.hub.welcomeBody', 'Pick any chapter to learn how your platform works. Each takes about 30 seconds.')}
            </p>

            {/* Progress */}
            <div className="mb-10">
              <div className="mb-2.5 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                  {t('tour.hub.chaptersComplete', '{{completed}} of {{total}} chapters complete', { completed: completedCount, total: totalCount })}
                </span>
                {completedCount > 0 && (
                  <Button variant="ghost" size="xs" onClick={resetTour}>
                    {t('tour.hub.resetProgress', 'Reset progress')}
                  </Button>
                )}
              </div>
              <div className="h-[3px] w-full overflow-hidden rounded-full bg-[var(--gold-border)]">
                <div
                  className="h-full rounded-full bg-[var(--green)] transition-all duration-700 ease-out"
                  style={{ width: `${(completedCount / totalCount) * 100}%` }}
                />
              </div>
            </div>
          </>
        )}

        {/* Chapter grid - cards laid out in rows of 4, group panels inserted between rows */}
        {(() => {
          const rendered = new Set<string>()
          const allCards: { type: 'card' | 'group'; node: ReactNode; groupKey?: string }[] = []

          for (const chapter of visibleChapters) {
            if (chapter.group) {
              if (rendered.has(chapter.group)) continue
              rendered.add(chapter.group)

              const groupChapters = groups.get(chapter.group) ?? []
              const groupCompleted = groupChapters.filter((c) => !c.comingSoon && completedChapters.has(c.id)).length
              const groupTotal = groupChapters.filter((c) => !c.comingSoon).length
              const groupAllDone = groupCompleted >= groupTotal
              const isExpanded = expandedGroup === chapter.group
              const firstChapter = groupChapters[0]
              const Icon = firstChapter.icon

              allCards.push({
                type: 'group',
                groupKey: chapter.group,
                node: (
                  <button
                    key={`group-${chapter.group}`}
                    onClick={() => setExpandedGroup(isExpanded ? null : chapter.group!)}
                    className={cn(
                      'group relative flex flex-col rounded-[12px] border-[0.5px] p-5 text-left transition-all duration-300',
                      isExpanded
                        ? 'border-[var(--gold-border)] bg-[var(--gold-bg)] shadow-[0_12px_40px_rgba(26,26,24,0.08)]'
                        : groupAllDone
                          ? 'cursor-pointer border-[var(--gold-border)] bg-[var(--gold-bg)] hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(26,26,24,0.08)]'
                          : 'cursor-pointer border-[var(--rule)] bg-white hover:-translate-y-1 hover:border-[var(--gold-border)] hover:shadow-[0_12px_40px_rgba(26,26,24,0.08)]',
                    )}
                  >
                    {groupAllDone && !isExpanded && (
                      <div className="absolute top-4 right-4 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--green)]">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                    {isExpanded && (
                      <ChevronDown className="absolute top-4 right-4 h-4 w-4 rotate-180 text-[var(--gold-ink)]" />
                    )}
                    <div
                      className={cn(
                        'mb-4 flex h-11 w-11 items-center justify-center rounded-[10px]',
                        isExpanded
                          ? 'bg-white text-[var(--gold-ink)]'
                          : groupAllDone
                            ? 'bg-white text-[var(--green)]'
                            : 'bg-[var(--bg-subtle)] text-[var(--ink-3)] group-hover:bg-[var(--gold-bg)] group-hover:text-[var(--gold-ink)]',
                      )}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                    <p className="mb-1.5 text-[14px] font-medium text-[var(--ink)]">{t('tour.group.claims.title', 'Claims')}</p>
                    <p className="mb-4 flex-1 text-[12px] leading-relaxed text-[var(--ink-3)]">
                      {t('tour.group.claims.description', 'From filing to payment - the lifecycle every claim follows, the rules and pricing that drive decisions, and the configuration behind it all')}
                    </p>
                    <span className={cn('text-[10px] uppercase tracking-[0.08em]', groupAllDone ? 'text-[var(--gold-ink)]' : 'text-[var(--ink-3)]')}>
                      {groupAllDone
                        ? t('tour.hub.complete', 'Complete')
                        : t('tour.hub.groupComplete', '{{completed}} of {{total}} complete', { completed: groupCompleted, total: groupTotal })}
                    </span>
                  </button>
                ),
              })
            } else {
              allCards.push({
                type: 'card',
                node: (
                  <ChapterCard
                    key={chapter.id}
                    chapter={chapter}
                    isComplete={completedChapters.has(chapter.id)}
                    isSoon={chapter.comingSoon === true}
                    onStart={() => handleStart(chapter)}
                  />
                ),
              })
            }
          }

          // Split into rows of 4, inserting expanded panels after the row containing the group card
          const rows: ReactNode[] = []
          for (let i = 0; i < allCards.length; i += 4) {
            const rowCards = allCards.slice(i, i + 4)
            rows.push(
              <div key={`row-${i}`} className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {rowCards.map((c) => c.node)}
              </div>,
            )

            // Check if any group card in this row is expanded
            for (const card of rowCards) {
              if (card.type === 'group' && card.groupKey && expandedGroup === card.groupKey) {
                const groupChapters = groups.get(card.groupKey) ?? []
                const groupCompleted = groupChapters.filter((c) => !c.comingSoon && completedChapters.has(c.id)).length
                const groupTotal = groupChapters.filter((c) => !c.comingSoon).length

                rows.push(
                  <div key={`expand-${card.groupKey}`} className="rounded-[12px] border-[0.5px] border-[var(--gold-border)] bg-[var(--gold-bg)] p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-[11px] uppercase tracking-[0.1em] text-[var(--gold-ink)]">
                        {t('tour.hub.groupWalkthroughsComplete', '{{completed}} of {{total}} walkthroughs complete', { completed: groupCompleted, total: groupTotal })}
                      </span>
                      <button
                        onClick={() => setExpandedGroup(null)}
                        className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                      >
                        {t('tour.hub.close', 'Close')}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {groupChapters.map((sub) => (
                        <ChapterCard
                          key={sub.id}
                          chapter={sub}
                          isComplete={completedChapters.has(sub.id)}
                          isSoon={sub.comingSoon === true}
                          onStart={() => handleStart(sub)}
                        />
                      ))}
                    </div>
                  </div>,
                )
              }
            }
          }

          return (
            <div className="flex flex-col gap-5">
              {rows}
            </div>
          )
        })()}

        {/* Footer hint */}
        {!allDone && (
          <p className="mt-10 text-center text-[12px] text-[var(--ink-3)]">
            {t('tour.hub.footerHint', 'You can reopen this guide anytime from the Platform Guide button in the top navigation.')}
          </p>
        )}
      </main>
    </AppShell>
  )
}
